import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { getReceiptColumnName, normalizeFormForReceiptLabel } from '../../utils/receiptFormLabel';

// For static assets, use ASSET_URL in dev, hostname in prod
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

function CampaignEntries() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  /** Active campaign form (for receipt upload label → entries table column name). */
  const [entryFormForReceipt, setEntryFormForReceipt] = useState(null);
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [entryStats, setEntryStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    winners: 0
  });
  const formatCount = (value) => Number(value ?? 0).toLocaleString();
  const formatStatusLabel = (status) => {
    if (!status) return '-';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'winner':
        return 'bg-purple-100 text-purple-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };
  const getVideoRecordingUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'object' && value.type === 'video_recording' && typeof value.url === 'string') {
      return value.url;
    }
    if (typeof value === 'string' && value.includes('/video-recording/')) {
      return value;
    }
    return '';
  };
  const getEntryFormResponses = (entry) => {
    const combinedValues = {
      ...(entry?.form_data || {}),
      ...(entry?.dynamic_form_data || {})
    };

    return Object.entries(combinedValues).filter(([key, value]) => {
      if (key === 'entry_method') return false;
      if (Array.isArray(value)) return value.length > 0;
      if (getVideoRecordingUrl(value)) return true;
      return value && value !== '';
    });
  };
  const renderFormResponseValue = (value) => {
    const videoUrl = getVideoRecordingUrl(value);
    if (videoUrl) {
      const resolvedVideoUrl = `${ASSET_BASE_URL}${videoUrl}`;
      return (
        <div className="flex flex-col gap-3">
          <video
            controls
            src={resolvedVideoUrl}
            className="w-full rounded-lg bg-black"
          />
          <a
            href={resolvedVideoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal-600 hover:text-teal-800 self-end"
          >
            Open video
          </a>
        </div>
      );
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  };
  
  // Notification states
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const entriesPerPage = 20;

  // Instant Winner Assignment states
  const [showInstantWinModal, setShowInstantWinModal] = useState(false);
  const [availablePrizes, setAvailablePrizes] = useState([]);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [assigningWinner, setAssigningWinner] = useState(false);

  // Gift Card Assignment states
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [giftCardTiers, setGiftCardTiers] = useState([]);
  const [selectedGiftCardTierId, setSelectedGiftCardTierId] = useState('');
  const [giftCardTiersLoading, setGiftCardTiersLoading] = useState(false);
  const [assigningGiftCard, setAssigningGiftCard] = useState(false);

  useEffect(() => {
    fetchCampaignAndEntries(1);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const formsRes = await api.get(`/admin/forms?campaign_id=${id}`);
        const active = (formsRes.data.forms || []).find((f) => f.is_active);
        if (!active?.id) {
          if (!cancelled) setEntryFormForReceipt(null);
          return;
        }
        const formRes = await api.get(`/admin/forms/${active.id}`);
        if (!cancelled) {
          setEntryFormForReceipt(normalizeFormForReceiptLabel(formRes.data.form));
        }
      } catch {
        if (!cancelled) setEntryFormForReceipt(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (campaign) {
      setCurrentPage(1);
    }
  }, [statusFilter, activeSearchQuery]);

  // Fetch entries when page changes or filters change
  useEffect(() => {
    if (campaign) {
      fetchCampaignAndEntries(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, activeSearchQuery, campaign]);

  const fetchCampaignAndEntries = async (page = 1) => {
    try {
      // Fetch campaign details if not already loaded
      if (!campaign) {
        const campaignResponse = await api.get(`/admin/campaigns/${id}`);
        setCampaign(campaignResponse.data.campaign);
      }

      // Fetch entries with pagination and filters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', entriesPerPage);
      
      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Add search query if provided
      if (activeSearchQuery && activeSearchQuery.trim()) {
        params.append('search', activeSearchQuery.trim());
      }
      
      const entriesResponse = await api.get(`/admin/campaigns/${id}/entries?${params.toString()}`);
      setEntries(entriesResponse.data.entries);
      
      if (entriesResponse.data.stats) {
        const stats = entriesResponse.data.stats;
        const normalizedStats = {
          total: Number(stats.total ?? 0),
          pending: Number(stats.pending ?? 0),
          approved: Number(stats.approved ?? 0),
          rejected: Number(stats.rejected ?? 0),
          winners: Number(stats.winners ?? 0)
        };
        setEntryStats(normalizedStats);
        setTotalEntries(normalizedStats.total);
      } else {
        // Fallback if backend stats are unavailable
        const fallbackEntries = entriesResponse.data.entries || [];
        const fallbackTotals = {
          total: Number(entriesResponse.data.pagination?.totalEntries ?? fallbackEntries.length ?? 0),
          pending: fallbackEntries.filter(e => e.status === 'pending').length,
          approved: fallbackEntries.filter(e => e.status === 'approved').length,
          rejected: fallbackEntries.filter(e => e.status === 'rejected').length,
          winners: fallbackEntries.filter(e => e.instant_win_won || e.status === 'winner').length
        };
        setEntryStats(fallbackTotals);
        setTotalEntries(fallbackTotals.total);
      }
      
      // Fetch products for product name mapping
      try {
        const productsResponse = await api.get(`/admin/campaigns/${id}`);
        if (productsResponse.data.products) {
          setProducts(productsResponse.data.products);
        }
      } catch (productErr) {
        console.error('Error fetching products:', productErr);
      }
      
      // Update pagination info if available
      if (entriesResponse.data.pagination) {
        setCurrentPage(entriesResponse.data.pagination.page);
        setTotalPages(entriesResponse.data.pagination.totalPages);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load campaign entries');
      setLoading(false);
    }
  };

  const handleStatusChange = async (entryId, newStatus, currentStatus) => {
    console.log('🔍 handleStatusChange called:', { entryId, newStatus, currentStatus, campaignId: id });
    console.log('🔍 Campaign data:', { name: campaign?.name, mechanics_type: campaign?.mechanics_type, mechanics_type_id: campaign?.mechanics_type_id });
    try {
      // If the status hasn't actually changed, don't update (except winner→approved which we treat as approved in the dropdown)
      if (currentStatus === newStatus && currentStatus !== 'winner') {
        console.log('⏭️ Skipping — status unchanged:', currentStatus, '→', newStatus);
        return; // No change needed
      }

      // If rejecting, prompt for rejection reason
      if (newStatus === 'rejected') {
        const { value: rejectionReason } = await Swal.fire({
          title: 'Reject Entry',
          text: 'Please provide a reason for rejecting this entry:',
          input: 'textarea',
          inputPlaceholder: 'Enter rejection reason...',
          inputAttributes: {
            'aria-label': 'Rejection reason'
          },
          showCancelButton: true,
          confirmButtonText: 'Reject Entry',
          confirmButtonColor: '#dc2626',
          cancelButtonText: 'Cancel',
          inputValidator: (value) => {
            if (!value || value.trim().length === 0) {
              return 'Please provide a rejection reason';
            }
            if (value.trim().length < 10) {
              return 'Rejection reason must be at least 10 characters long';
            }
          }
        });

        if (!rejectionReason) {
          return; // User cancelled
        }

        console.log('📡 Sending PATCH (rejected):', `/admin/campaigns/${id}/entries/${entryId}/status`);
        const response = await api.patch(`/admin/campaigns/${id}/entries/${entryId}/status`, {
          status: newStatus,
          rejection_reason: rejectionReason.trim()
        });
        console.log('✅ PATCH response:', response.status, response.data);
      } else {
        console.log('📡 Sending PATCH:', `/admin/campaigns/${id}/entries/${entryId}/status`, { status: newStatus });
        const response = await api.patch(`/admin/campaigns/${id}/entries/${entryId}/status`, { status: newStatus });
        console.log('✅ PATCH response:', response.status, response.data);
      }

      // Refresh entries list on current page
      fetchCampaignAndEntries(currentPage);
    } catch (err) {
      console.error('❌ Update status error:', err);
      console.error('❌ Error response:', err.response?.status, err.response?.data);
      alert('Failed to update entry status');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This entry will be permanently deleted. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/campaigns/${id}/entries/${entryId}`);
        
        // Show success message
        await Swal.fire({
          title: 'Deleted!',
          text: 'Entry has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#0f766e'
        });
        
        // Refresh entries list
        fetchCampaignAndEntries();
      } catch (err) {
        console.error('Delete entry error:', err);
        await Swal.fire({
          title: 'Error!',
          text: err.response?.data?.error || 'Failed to delete entry',
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      }
    }
  };

  const handleSelectEntry = (entryId) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === filteredEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(filteredEntries.map(entry => entry.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.size === 0) return;
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${selectedEntries.size} ${selectedEntries.size === 1 ? 'entry' : 'entries'}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        await api.delete(`/admin/campaigns/${id}/entries/bulk`, {
          data: { entryIds: Array.from(selectedEntries) }
        });
        
        const deletedCount = selectedEntries.size;
        setSelectedEntries(new Set());
        fetchCampaignAndEntries(currentPage);
        
        // Show success message
        await Swal.fire({
          title: 'Deleted!',
          text: `Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'entry' : 'entries'}.`,
          icon: 'success',
          confirmButtonColor: '#0f766e'
        });
      } catch (err) {
        console.error('Bulk delete error:', err);
        await Swal.fire({
          title: 'Error!',
          text: err.response?.data?.error || 'Failed to delete entries',
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Normalize to YYYY-MM-DD.
      // Inputs we see in the wild:
      // - ISO (2025-11-03T06:03:07.000Z)
      // - MySQL datetime (2025-11-06 15:27:35)
      // - Date only (2025-11-18)
      // - AU slashes (16/06/2023) sometimes coming from legacy/metadata
      const raw = String(dateString).trim();
      const [firstToken] = raw.split(/\s+/);

      // DD/MM/YYYY (optionally with time after it)
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(firstToken)) {
        const [dd, mm, yyyy] = firstToken.split('/');
        return `${yyyy}-${mm}-${dd}`;
      }

      if (dateString.includes('T')) {
        // ISO format - convert from UTC to Sydney time and display
        const date = new Date(dateString);
        
        if (isNaN(date.getTime())) {
          console.error('Invalid ISO date:', dateString);
          return '-';
        }
        
        // Format in Sydney timezone as YYYY-MM-DD
        return date.toLocaleDateString('en-CA', {
          timeZone: 'Australia/Sydney',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } else {
        // MySQL format or date-only - take the date part (already in Sydney time)
        const [datePart] = raw.split(' ');
        
        if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          console.error('Invalid non-ISO date format:', dateString);
          return '-';
        }

        return datePart;
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '-';
    }
  };


  const handleViewMetadata = (entry) => {
    setSelectedEntry(entry);
    setShowMetadataModal(true);
  };

  const closeMetadataModal = () => {
    setShowMetadataModal(false);
    setSelectedEntry(null);
  };

  const handleMarkAsInstantWinner = async (entry) => {
    try {
      // Fetch available instant win prizes for this campaign
      const response = await api.get(`/admin/campaigns/${id}/instant-win/prizes`);
      const prizes = response.data.prizes || [];
      
      // Filter to only show prizes with remaining quantity
      const availablePrizesWithStock = prizes.filter(prize => 
        prize.remaining_quantity > 0 && prize.is_active
      );
      
      if (availablePrizesWithStock.length === 0) {
        await Swal.fire({
          title: 'No Prizes Available',
          text: 'There are no instant win prizes available for this campaign.',
          icon: 'warning',
          confirmButtonColor: '#0f766e'
        });
        return;
      }
      
      setAvailablePrizes(availablePrizesWithStock);
      setSelectedEntry(entry);
      setShowInstantWinModal(true);
    } catch (error) {
      console.error('Error fetching prizes:', error);
      await Swal.fire({
        title: 'Error',
        text: 'Failed to load available prizes',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleAssignInstantWin = async () => {
    if (!selectedPrize || !selectedEntry) return;
    
    setAssigningWinner(true);
    try {
      await api.post(`/admin/campaigns/${id}/entries/${selectedEntry.id}/assign-instant-win`, {
        prize_id: selectedPrize.id
      });
      
      setShowInstantWinModal(false);
      setSelectedPrize(null);
      
      await Swal.fire({
        title: 'Success!',
        text: 'Entry has been marked as instant winner and email has been sent.',
        icon: 'success',
        confirmButtonColor: '#0f766e'
      });
      
      // Refresh entries list
      fetchCampaignAndEntries(currentPage);
      
      // If metadata modal is still open, close it
      if (showMetadataModal) {
        closeMetadataModal();
      }
    } catch (error) {
      console.error('Error assigning instant win:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.error || 'Failed to assign instant win prize',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setAssigningWinner(false);
    }
  };

  const resetGiftCardModal = () => {
    setShowGiftCardModal(false);
    setSelectedGiftCardTierId('');
    setGiftCardTiers([]);
  };

  const handleOpenGiftCardModal = async (entry) => {
    setSelectedEntry(entry);
    setGiftCardTiersLoading(true);
    setShowGiftCardModal(true);
    setSelectedGiftCardTierId('');

    try {
      const { data } = await api.get(`/admin/campaigns/${id}/cashback/gift-card-settings`);
      const availableTiers = (data.tiers || []).filter((tier) => {
        const maxWinners = Number(tier.max_winners ?? 0);
        const issuedCount = Number(tier.issued_count ?? 0);
        return Number.isFinite(maxWinners) && maxWinners > issuedCount;
      });

      setGiftCardTiers(availableTiers);
      if (availableTiers.length > 0) {
        const firstTier = availableTiers[0];
        setSelectedGiftCardTierId(String(firstTier.id));
      }
    } catch (error) {
      console.error('Error loading cashback gift card tiers:', error);
      resetGiftCardModal();
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.error || 'Failed to load cashback gift card tiers',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setGiftCardTiersLoading(false);
    }
  };

  const handleAssignGiftCard = async () => {
    if (!selectedGiftCardTierId || !selectedEntry) return;

    const selectedTier = giftCardTiers.find((tier) => String(tier.id) === String(selectedGiftCardTierId));
    if (!selectedTier) {
      await Swal.fire({
        title: 'Select Gift Card',
        text: 'Please select a cashback gift card tier.',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
      return;
    }
    
    const amount = parseFloat(selectedTier.amount);
    if (isNaN(amount) || amount <= 0) {
      await Swal.fire({
        title: 'Invalid Amount',
        text: 'The selected cashback gift card tier has an invalid amount.',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
      return;
    }
    
    setAssigningGiftCard(true);
    try {
      await api.post(`/admin/campaigns/${id}/entries/${selectedEntry.id}/assign-gift-card`, {
        amount,
        settings_id: selectedTier.id
      });
      
      resetGiftCardModal();
      
      await Swal.fire({
        title: 'Success!',
        text: 'Gift card has been assigned and email has been sent.',
        icon: 'success',
        confirmButtonColor: '#0f766e'
      });
      
      // Refresh entries list first
      await fetchCampaignAndEntries(currentPage);
      
      // Refresh metadata modal if still open - fetch the specific entry to get updated gift card info
      if (showMetadataModal && selectedEntry) {
        // Retry mechanism to find the updated entry
        let updatedEntry = null;
        let retries = 3;
        
        while (!updatedEntry && retries > 0) {
          try {
            // Small delay to ensure database is updated
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Fetch entries with current filters to find the updated entry
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('limit', entriesPerPage);
            
            if (statusFilter && statusFilter !== 'all') {
              params.append('status', statusFilter);
            }
            
            if (activeSearchQuery && activeSearchQuery.trim()) {
              params.append('search', activeSearchQuery.trim());
            }
            
            const entriesResponse = await api.get(`/admin/campaigns/${id}/entries?${params.toString()}`);
            updatedEntry = entriesResponse.data.entries.find(e => e.id === selectedEntry.id);
            
            if (updatedEntry) {
              console.log('Updated entry with gift card:', updatedEntry);
              setSelectedEntry(updatedEntry);
              break;
            }
            
            // If not found in current page, try fetching all entries without filters
            if (!updatedEntry && retries === 1) {
              const allEntriesResponse = await api.get(`/admin/campaigns/${id}/entries`);
              updatedEntry = allEntriesResponse.data.entries?.find(e => e.id === selectedEntry.id);
              if (updatedEntry) {
                console.log('Found entry with gift card (no filters):', updatedEntry);
                setSelectedEntry(updatedEntry);
                break;
              }
            }
            
            retries--;
          } catch (refreshError) {
            console.error('Error refreshing entry details:', refreshError);
            retries--;
          }
        }
        
        if (!updatedEntry) {
          console.warn('Entry not found after gift card assignment after retries:', selectedEntry.id);
        }
      }
    } catch (error) {
      console.error('Error assigning gift card:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.error || 'Failed to assign gift card',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setAssigningGiftCard(false);
    }
  };

  // With server-side pagination, we'll use all entries
  const filteredEntries = entries;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading entries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  const receiptColumnName = getReceiptColumnName(entryFormForReceipt);

  return (
    <AdminLayout activeSection="CAMPAIGN">
      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/admin/campaigns')}
          className="text-white hover:text-gray-200 mr-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">CAMPAIGN ENTRIES</h1>
          {campaign && (
            <p className="text-white/80 text-lg">{campaign.name}</p>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">Total Entries</dt>
                    <dd className="text-2xl font-bold text-gray-900 mt-1">{formatCount(entryStats.total)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">Pending</dt>
                    <dd className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCount(entryStats.pending)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">Approved</dt>
                    <dd className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCount(entryStats.approved)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">Rejected</dt>
                    <dd className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCount(entryStats.rejected)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-600 truncate">Winners</dt>
                    <dd className="text-2xl font-bold text-gray-900 mt-1">
                      {formatCount(entryStats.winners)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="flex-1 w-full md:w-auto">
              <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-transparent overflow-hidden">
                {/* Icon Container */}
                <div className="flex items-center px-3 bg-gray-50 border-r border-gray-300">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {/* Input Field */}
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or entry number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setActiveSearchQuery(searchQuery);
                      setCurrentPage(1);
                    }
                  }}
                  className="flex-1 px-3 py-2 border-0 focus:ring-0 focus:outline-none"
                />
                {/* Clear Button */}
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveSearchQuery('');
                      setCurrentPage(1);
                    }}
                    className="flex items-center px-3 hover:bg-gray-50 transition-colors"
                    type="button"
                  >
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {/* Search Button */}
                <button
                  onClick={() => {
                    setActiveSearchQuery(searchQuery);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 transition-colors font-medium"
                  type="button"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="flex items-center space-x-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'pending'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setStatusFilter('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'approved'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setStatusFilter('rejected')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'rejected'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Rejected
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedEntries.size > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {selectedEntries.size} {selectedEntries.size === 1 ? 'entry' : 'entries'} selected
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>{isDeleting ? 'Deleting...' : 'Delete Selected'}</span>
            </button>
          </div>
        )}

        {/* Entries Table */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No entries yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Entries will appear here once users start participating in the campaign.
              </p>
              {campaign?.status === 'draft' && (
                <p className="mt-2 text-sm text-yellow-600">
                  Note: This campaign is still in draft mode. Activate it to start receiving entries.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-teal-800 text-white">
                    <th className="px-3 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={filteredEntries.length > 0 && selectedEntries.size === filteredEntries.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                      Entry Details
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                      {receiptColumnName}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                      Newsletter
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                      Gift Card
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedEntries.has(entry.id)}
                          onChange={() => handleSelectEntry(entry.id)}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">
                          #{entry.submission_order || entry.entry_number}
                          {entry.instant_win_won ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                              Winner
                            </span>
                          ) : null}
                        </div>
                        <div className="text-gray-500 text-xs">{formatDate(entry.created_at)}</div>
                        {entry.instant_win_won ? (
                          <div className="text-xs text-yellow-700 mt-1">
                            Prize: {entry.instant_win_prize}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{entry.entry_data?.name || entry.user_name || '-'}</div>
                        <div className="text-gray-500">{entry.entry_data?.email || entry.user_email || '-'}</div>
                        {entry.entry_data?.phone && (
                          <div className="text-gray-400 text-xs">{entry.entry_data.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.validated_product_ids && entry.validated_product_ids.length > 0 ? (
                          entry.validated_product_ids.map(productId => {
                            const product = products.find(p => p.id === productId);
                            return product ? product.name : `Product ID: ${productId}`;
                          }).join(', ')
                        ) : (
                          entry.product_name || '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.receipt_url ? (
                          <div className="flex flex-col space-y-1">
                            <a 
                              href={`${ASSET_BASE_URL}${entry.receipt_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-teal-600 hover:text-teal-800 font-medium"
                            >
                              View {receiptColumnName}
                            </a>
                            {entry.receipt_confidence && (
                              <span className={`text-xs ${
                                entry.receipt_confidence >= 0.7 
                                  ? 'text-green-600' 
                                  : 'text-yellow-600'
                              }`}>
                                Confidence: {Math.round(entry.receipt_confidence * 100)}%
                                {entry.receipt_confidence < 0.7 && ' ⚠️'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">No {receiptColumnName.toLowerCase()}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {entry.newsletter_consent ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Subscribed
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {entry.gift_card_id ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.gift_card_status === 'sent' ? 'bg-green-100 text-green-800' :
                              entry.gift_card_status === 'redeemed' ? 'bg-blue-100 text-blue-800' :
                              entry.gift_card_status === 'expired' ? 'bg-gray-100 text-gray-800' :
                              entry.gift_card_status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {formatStatusLabel(entry.gift_card_status || 'sent')}
                            </span>
                            <span className="text-xs font-medium text-gray-900">
                              ${parseFloat(entry.gift_card_amount || 0).toFixed(2)}
                            </span>
                            {entry.gift_card_sent_at ? (
                              <span className="text-xs text-gray-500">
                                Sent {formatDate(entry.gift_card_sent_at)}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <select
                            value={entry.status === 'winner' ? 'approved' : entry.status}
                            onChange={(e) => handleStatusChange(entry.id, e.target.value, entry.status)}
                            className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                          {(getEntryFormResponses(entry).length > 0) || entry.receipt_metadata ? (
                            <button
                              onClick={() => handleViewMetadata(entry)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View all fields"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                          ) : null}
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete entry"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Footer with Pagination */}
          <div className="px-6 py-4 flex justify-end items-center border-t border-gray-200">
            <div className="flex items-center space-x-4">
              {totalPages > 1 && (
                <>
                  <span className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
              <span className="text-sm font-medium text-gray-700">
                {totalEntries > 0 ? (
                  <>
                    Showing {Math.min(entriesPerPage * (currentPage - 1) + 1, totalEntries)}-
                    {Math.min(entriesPerPage * currentPage, totalEntries)} of {totalEntries} entries
                  </>
                ) : (
                  'No entries'
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Modal */}
      {showMetadataModal && selectedEntry && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-800 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <h3 className="text-xl font-semibold text-white">Entry Details</h3>
                  {!selectedEntry?.instant_win_won && Boolean(campaign?.instant_win_enabled) && (
                    <button
                      onClick={() => handleMarkAsInstantWinner(selectedEntry)}
                      className="inline-flex items-center px-3 py-1.5 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                      title="Mark as Instant Winner"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      Mark as Instant Winner
                    </button>
                  )}
                  {!selectedEntry?.gift_card_id && (
                    <button
                      onClick={() => handleOpenGiftCardModal(selectedEntry)}
                      className="inline-flex items-center px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                      title="Assign Cashback Gift Card"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Gift Card
                    </button>
                  )}
                </div>
                <button
                  onClick={closeMetadataModal}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              {/* Entry Number at Top */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <span className="text-sm font-medium text-gray-600">Entry Number</span>
                </div>
                <p className="text-lg font-mono font-semibold text-gray-900 mt-1">{selectedEntry.entry_number}</p>
                <p className="text-sm text-gray-500 mt-1">Submitted on {formatDate(selectedEntry.created_at)}</p>
              </div>

              {/* Entry Status */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Entry Status
                </h4>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full mt-1 ${getStatusBadgeClasses(selectedEntry.status)}`}>
                      {formatStatusLabel(selectedEntry.status)}
                    </span>
                  </div>
                  {selectedEntry.status === 'rejected' && selectedEntry.rejection_reason ? (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Rejection Reason</p>
                      <p className="text-sm font-medium text-gray-900 mt-1 whitespace-pre-line">
                        {selectedEntry.rejection_reason}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Instant Win Banner */}
              {selectedEntry.instant_win_won ? (
                <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-6 h-6 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                    Instant Win Prize Winner!
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Prize</p>
                      <p className="font-semibold text-gray-900">{selectedEntry.instant_win_prize}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Value</p>
                      <p className="font-semibold text-gray-900">${selectedEntry.instant_win_value}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Voucher Code</p>
                      <p className="font-mono text-sm bg-white px-2 py-1 rounded border border-gray-200">{selectedEntry.instant_win_claim_code}</p>
                    </div>
                  </div>
                  {selectedEntry.instant_win_claimed_at && (
                    <p className="text-sm text-gray-600 mt-3">
                      <span className="font-medium">Claimed:</span> {formatDate(selectedEntry.instant_win_claimed_at)}
                    </p>
                  )}
                </div>
              ) : null}

              {/* Gift Card Banner */}
              {selectedEntry.gift_card_id ? (
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Gift Card Assigned
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="font-semibold text-gray-900">${parseFloat(selectedEntry.gift_card_amount || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Gift Card Code</p>
                      <p className="font-mono text-sm bg-white px-2 py-1 rounded border border-gray-200">{selectedEntry.gift_card_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedEntry.gift_card_status === 'sent' ? 'bg-green-100 text-green-800' :
                        selectedEntry.gift_card_status === 'redeemed' ? 'bg-blue-100 text-blue-800' :
                        selectedEntry.gift_card_status === 'expired' ? 'bg-gray-100 text-gray-800' :
                        selectedEntry.gift_card_status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedEntry.gift_card_status || 'pending'}
                      </span>
                    </div>
                  </div>
                  {selectedEntry.gift_card_expires_at && (
                    <p className="text-sm text-gray-600 mt-3">
                      <span className="font-medium">Expires:</span> {formatDate(selectedEntry.gift_card_expires_at)}
                    </p>
                  )}
                  {selectedEntry.gift_card_sent_at && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Sent:</span> {formatDate(selectedEntry.gift_card_sent_at)}
                    </p>
                  )}
                  {selectedEntry.gift_card_order_id && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Order ID:</span> {selectedEntry.gift_card_order_id}
                    </p>
                  )}
                </div>
              ) : null}

              {/* User Information Section */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  User Information
                </h4>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-medium text-gray-900">{selectedEntry.entry_data?.name || selectedEntry.user_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email Address</p>
                    <p className="font-medium text-gray-900">{selectedEntry.entry_data?.email || selectedEntry.user_email || '-'}</p>
                  </div>
                  {selectedEntry.entry_data?.phone && (
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="font-medium text-gray-900">{selectedEntry.entry_data.phone}</p>
                    </div>
                  )}
                  {selectedEntry.dynamic_form_data?.entry_method && (
                    <div>
                      <p className="text-sm text-gray-600">Entry Method</p>
                      <p className="font-medium text-gray-900 capitalize">
                        {selectedEntry.dynamic_form_data.entry_method.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Form Fields */}
              {getEntryFormResponses(selectedEntry).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Form Responses
                  </h4>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    {getEntryFormResponses(selectedEntry).map(([key, value]) => (
                      <div
                        key={key}
                        className={getVideoRecordingUrl(value)
                          ? 'space-y-3'
                          : 'flex justify-between items-start gap-4'}
                      >
                        <span className="text-sm text-gray-600">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <div className={getVideoRecordingUrl(value)
                          ? 'text-sm font-medium text-gray-900'
                          : 'text-sm font-medium text-gray-900 ml-4 text-right'}
                        >
                          {renderFormResponseValue(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Receipt Information Section */}
              {(selectedEntry.receipt_url || selectedEntry.receipt_metadata) && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {receiptColumnName} Data
                  </h4>
                  
                  {/* Receipt Status and Link */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{receiptColumnName} Status</p>
                        <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                          selectedEntry.receipt_status === 'valid' ? 'bg-green-100 text-green-800' :
                          selectedEntry.receipt_status === 'invalid' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {selectedEntry.receipt_status || 'Pending Review'}
                        </span>
                      </div>
                      {selectedEntry.receipt_url && (
                        <a 
                          href={`${ASSET_BASE_URL}${selectedEntry.receipt_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View {receiptColumnName}
                        </a>
                      )}
                    </div>
                    
                    {selectedEntry.receipt_confidence && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600">Match Confidence</p>
                        <div className="flex items-center mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                            <div 
                              className={`h-2 rounded-full ${
                                selectedEntry.receipt_confidence >= 0.7 ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                              style={{ width: `${selectedEntry.receipt_confidence * 100}%` }}
                            />
                          </div>
                          <span className={`text-sm font-medium ${
                            selectedEntry.receipt_confidence >= 0.7 ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            {Math.round(selectedEntry.receipt_confidence * 100)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Store Information */}
                  {selectedEntry.receipt_metadata?.store && (
                    <div className="bg-blue-50 rounded-xl p-4 mb-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Store Information</p>
                      {selectedEntry.receipt_metadata.store.name && (
                        <p className="text-sm text-gray-900"><span className="text-gray-600">Name:</span> {selectedEntry.receipt_metadata.store.name}</p>
                      )}
                      {selectedEntry.receipt_metadata.store.address && (
                        <p className="text-sm text-gray-900"><span className="text-gray-600">Address:</span> {selectedEntry.receipt_metadata.store.address}</p>
                      )}
                      {selectedEntry.receipt_metadata.store.phone && (
                        <p className="text-sm text-gray-900"><span className="text-gray-600">Phone:</span> {selectedEntry.receipt_metadata.store.phone}</p>
                      )}
                    </div>
                  )}

                  {/* Transaction Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {selectedEntry.receipt_metadata?.date && (
                      <div className="bg-purple-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Purchase Date</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(selectedEntry.receipt_metadata.date)}
                        </p>
                      </div>
                    )}
                    {selectedEntry.receipt_metadata?.transactionNumber && (
                      <div className="bg-purple-50 rounded-xl p-4">
                        <p className="text-sm text-gray-600">Transaction #</p>
                        <p className="font-medium text-gray-900">{selectedEntry.receipt_metadata.transactionNumber}</p>
                      </div>
                    )}
                  </div>

                  {/* Price Information */}
                  {selectedEntry.receipt_metadata?.prices && (
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Purchase Summary</p>
                      
                      {selectedEntry.receipt_metadata.prices.items && selectedEntry.receipt_metadata.prices.items.length > 0 && (
                        <div className="space-y-2 mb-3 pb-3 border-b border-green-200">
                          {selectedEntry.receipt_metadata.prices.items.map((item, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-700">{item.product}</span>
                              <span className="font-medium text-gray-900">${item.price.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="space-y-1">
                        {selectedEntry.receipt_metadata.prices.subtotal && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="text-gray-900">${selectedEntry.receipt_metadata.prices.subtotal.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedEntry.receipt_metadata.prices.tax && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Tax</span>
                            <span className="text-gray-900">${selectedEntry.receipt_metadata.prices.tax.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedEntry.receipt_metadata.prices.total && (
                          <div className="flex justify-between text-base font-semibold pt-2 border-t border-green-200">
                            <span className="text-gray-700">Total</span>
                            <span className="text-gray-900">${selectedEntry.receipt_metadata.prices.total.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Validation Feedback */}
                  {selectedEntry.receipt_metadata?.validationFeedback && (
                    <div className={`rounded-xl p-4 mt-4 ${
                      selectedEntry.receipt_metadata.validationFeedback.success 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Validation Result</p>
                      <p className={`text-sm ${
                        selectedEntry.receipt_metadata.validationFeedback.success 
                          ? 'text-green-800' 
                          : 'text-yellow-800'
                      }`}>
                        {selectedEntry.receipt_metadata.validationFeedback.message}
                      </p>
                      {selectedEntry.receipt_metadata.validationFeedback.confidence < 0.7 && (
                        <p className="mt-2 text-xs text-yellow-700">
                          ⚠️ {receiptColumnName} quality issues detected. Customer should be advised to submit a clearer image or PDF for faster approval.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instant Win Prize Selection Modal */}
      {showInstantWinModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Select Instant Win Prize</h3>
                <button
                  onClick={() => {
                    setShowInstantWinModal(false);
                    setSelectedPrize(null);
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700">
                  Select a prize to assign to this entry. An instant win email will be sent automatically.
                </p>
              </div>

              {/* Prize List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availablePrizes.map((prize) => (
                  <div
                    key={prize.id}
                    onClick={() => setSelectedPrize(prize)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPrize?.id === prize.id
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          {prize.logo_url && (
                            <img
                              src={`${ASSET_BASE_URL}${prize.logo_url}`}
                              alt={prize.prize_name}
                              className="h-12 w-auto object-contain"
                            />
                          )}
                          <div>
                            <h4 className="font-semibold text-gray-900">{prize.prize_name}</h4>
                            <p className="text-sm text-gray-600">Value: ${prize.prize_value}</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Available</p>
                        <p className="font-semibold text-gray-900">{prize.remaining_quantity}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowInstantWinModal(false);
                    setSelectedPrize(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={assigningWinner}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignInstantWin}
                  disabled={!selectedPrize || assigningWinner}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {assigningWinner ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Assigning...
                    </div>
                  ) : (
                    'Assign Prize'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gift Card Assignment Modal */}
      {showGiftCardModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Assign Gift Card</h3>
                <button
                  onClick={resetGiftCardModal}
                  className="text-white/80 hover:text-white transition-colors"
                  disabled={assigningGiftCard}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-4">
                  Select one of this campaign's cashback gift cards. A gift card will be created and an email will be sent automatically.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cashback Gift Card
                  </label>
                  <select
                    value={selectedGiftCardTierId}
                    onChange={(e) => {
                      const tierId = e.target.value;
                      setSelectedGiftCardTierId(tierId);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={assigningGiftCard || giftCardTiersLoading}
                  >
                    <option value="">
                      {giftCardTiersLoading ? 'Loading cashback gift cards...' : 'Select a cashback gift card'}
                    </option>
                    {giftCardTiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        ${parseFloat(tier.amount || 0).toFixed(2)} ({Number(tier.max_winners || 0) - Number(tier.issued_count || 0)} remaining)
                      </option>
                    ))}
                  </select>
                  {!giftCardTiersLoading && giftCardTiers.length === 0 ? (
                    <p className="text-sm text-red-600 mt-2">
                      No cashback gift cards are available for this campaign.
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={resetGiftCardModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={assigningGiftCard}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignGiftCard}
                  disabled={!selectedGiftCardTierId || assigningGiftCard || giftCardTiersLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {assigningGiftCard ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Assigning...
                    </div>
                  ) : (
                    'Assign Gift Card'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default CampaignEntries;
